import { useState, useEffect, useRef } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';

/* ── Founder's Message Quote Card ── */
function FounderMessage() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [typedIndex, setTypedIndex] = useState(0);
  const quote = "我们相信，金融的未来属于那些能够将数据科学与人文洞察深度融合的机构。在香港这座连接东西方的桥梁上，蚂蚁链控股集团将以技术为引擎、合规为基石、人才为核心，为全球投资者创造穿越周期的长期价值。";

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (typedIndex >= quote.length) return;
    const timer = setTimeout(() => setTypedIndex(i => i + 1), 35);
    return () => clearTimeout(timer);
  }, [visible, typedIndex]);

  return (
    <div ref={ref} className="relative">
      {/* Outer glow ring */}
      <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-accent-500/5 via-primary-500/5 to-accent-500/5 blur-xl animate-pulse-glow" style={{ animationDuration: '4s' }}></div>

      <div className="relative bg-background-100 rounded-2xl border border-background-300/40 overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-accent-500/60 via-primary-500/40 to-transparent"></div>

        <div className="p-8 md:p-12 lg:p-14">
          <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-14">

            {/* Left: Avatar + Identity */}
            <div className="shrink-0 flex flex-col items-center lg:items-start">
              <div className="relative">
                {/* Pulse ring around avatar */}
                <div className="absolute -inset-1.5 rounded-full border-2 border-accent-500/20 animate-pulse-glow" style={{ animationDuration: '2.5s' }}></div>
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden ring-2 ring-accent-500/30 relative z-10">
                  <img
                    src="https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/041ee6c9-00c0-4544-bb15-f0fdaa2ca6c6_compressed_2.webp"
                    alt="白子晨"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Status dot */}
                <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-background-100 z-20 animate-pulse-glow"></span>
              </div>
              <div className="mt-5 text-center lg:text-left">
                <h3 className="text-lg font-heading font-bold text-foreground-100">白子晨</h3>
                <p className="text-xs text-accent-500 font-medium mt-0.5">集团主席兼首席执行官</p>
                <p className="text-[10px] text-foreground-600 font-light mt-1">剑桥 · 斯坦福</p>
              </div>
            </div>

            {/* Right: Quote */}
            <div className="flex-1 min-w-0">
              {/* Quote icon */}
              <div className="mb-6">
                <i className="ri-double-quotes-l text-4xl md:text-5xl text-accent-500/25"></i>
              </div>

              {/* Quote text with typewriter effect */}
              <blockquote className="text-base md:text-lg lg:text-xl text-foreground-200 leading-relaxed font-light mb-8">
                &ldquo;{visible ? quote.slice(0, typedIndex) : ''}
                {visible && typedIndex < quote.length && <span className="inline-block w-0.5 h-5 bg-accent-400 animate-blink ml-0.5 align-middle"></span>}
                &rdquo;
              </blockquote>

              {/* Bottom line */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-6 border-t border-background-300/30">
                <span className="text-xs text-foreground-500 font-light tracking-wider">— 创始人寄语 · 香港</span>
                <span className="hidden sm:block w-px h-3 bg-background-400/30"></span>
                <span className="flex items-center gap-1.5 text-[10px] mono-data text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                  LIVE · 2026
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom decorative scan line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent"></div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 bg-dot-matrix opacity-30"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full border border-background-300/20 -translate-y-1/3 translate-x-1/4"></div>
        <div className="absolute top-[30%] right-[25%] w-2 h-2 rounded-full bg-accent-400/40 animate-pulse-glow"></div>
        <div className="absolute top-[45%] right-[40%] w-1.5 h-1.5 rounded-full bg-primary-400/40 animate-pulse-glow" style={{ animationDelay: '1s' }}></div>

        {/* Decorative chart lines */}
        <svg className="absolute bottom-0 left-0 w-full h-32 opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 Q100 80 200 100 Q300 30 400 80 Q500 50 600 90 Q700 40 800 70 Q900 30 1000 60 Q1100 20 1200 55 Q1300 25 1400 50" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
          <path d="M0 130 Q80 100 200 115 Q350 60 500 105 Q650 70 800 95 Q950 55 1100 85 Q1250 45 1400 75" fill="none" stroke="rgba(180,150,80,0.5)" strokeWidth="0.8" strokeDasharray="6 4" />
        </svg>

        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-accent-500 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-4">About Us</p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-100 leading-tight">关于我们</h1>
            <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed font-light">
              立足香港，连接全球。以量化科技驱动资产管理，以合规守护长期价值。
            </p>
          </div>
        </div>
      </section>

      {/* Group Background */}
      <section className="relative py-20 md:py-28 bg-background-100">
        <div className="absolute inset-0 bg-dot-matrix opacity-20"></div>
        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Group Profile</p>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100 mb-6">集团背景</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-400 leading-relaxed font-light">
                  <p>
                    香港蚂蚁链控股集团（Ant Chain Holding Group）成立于香港，是一家以量化科技与资产管理为核心的综合性金融控股集团。集团依托香港国际金融中心的区位优势与制度优势，聚焦量化研究、基金管理、家族办公室与资本投资四大核心板块，构建覆盖投资研究、资产管理、财富管理与资本运作的全链条服务体系。
                  </p>
                  <p>
                    集团秉持「量化驱动资管，连接全球资产价值」的核心理念，将人工智能、大数据分析等前沿技术与传统资产管理深度融合，为全球机构投资者与高净值家族提供专业化、定制化、合规稳健的资产管理解决方案。
                  </p>
                  <p>
                    集团旗下各业务主体均持有香港证监会（SFC）颁发的相关牌照，严格遵循香港及国际金融监管法规，建立完善的内部控制与风险管理体系。
                  </p>
                </div>

                {/* Live Metrics Bar */}
                <AboutMetrics />
              </div>
              <div className="relative">
                <div className="relative rounded-xl overflow-hidden border border-background-300/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20financial%20office%20interior%20with%20dark%20navy%20tones%2C%20glass%20walls%2C%20Hong%20Kong%20skyline%20visible%20through%20windows%20at%20dusk%2C%20professional%20corporate%20atmosphere%2C%20gold%20accent%20design%20elements%2C%20sophisticated%20fintech%20headquarters%2C%20no%20people%2C%20dramatic%20lighting&width=700&height=500&seq=about-office-dark-2026&orientation=landscape"
                    alt="集团办公环境"
                    className="w-full rounded-xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-50/40 to-transparent"></div>
                </div>
                {/* Decorative frame */}
                <div className="absolute -bottom-4 -left-4 w-full h-full rounded-xl border border-accent-500/15 -z-10"></div>
                {/* Data node */}
                <div className="absolute top-6 -right-3 w-3 h-3 rounded-full bg-accent-400/60 animate-pulse-glow"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategy & Culture */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Strategy &amp; Culture</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">战略与文化</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
              <AboutStrategyCard
                icon="ri-focus-3-line"
                title="战略定位"
                desc="立足香港国际金融中心，聚焦量化科技与资产管理核心赛道，以技术创新驱动差异化竞争优势，打造具有全球影响力的科技金融集团。"
                accent="border-l-primary-500"
                liveLabel="MARKET CAP"
                liveVal={128}
                liveSuffix="B+"
              />
              <AboutStrategyCard
                icon="ri-shield-check-line"
                title="合规治理"
                desc="建立集团统一的合规管理框架，严格执行香港证监会监管要求，持续完善反洗钱、风险管理与内部控制体系，确保所有业务合规运营。"
                accent="border-l-accent-500"
                liveLabel="LICENSES"
                liveVal={6}
                liveSuffix=""
              />
              <AboutStrategyCard
                icon="ri-heart-line"
                title="企业文化"
                desc={'倡导\u201C专业、稳健、创新、协作\u201D的核心价值观，培养跨学科的专业团队，营造开放包容的组织氛围，追求卓越与长期价值。'}
                accent="border-l-primary-500"
                liveLabel="TEAM SIZE"
                liveVal={150}
                liveSuffix="+"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Management */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Management Team</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">核心管理层</h2>
            </div>
            {/* ── Chairman Spotlight ── */}
            <ChairmanSpotlight />

            {/* ── Other Executives (3-column) ── */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '李文华', title: '首席投资官', desc: '前全球顶级量化对冲基金合伙人，拥有逾15年跨资产量化投资管理经验，曾主导管理规模超百亿美元的多策略投资组合。麻省理工学院金融工程硕士，香港科技大学数学系学士。在因子投资、系统性宏观策略与另类数据信号挖掘领域具有深厚造诣，其研发的多个量化策略连续五年实现两位数夏普比率。曾在纽约、伦敦、香港三地负责全球宏观与亚太市场策略，兼具国际化视野与本土市场洞察。', img: 'https://readdy.ai/api/search-image?query=Professional%20business%20portrait%20of%20Asian%20male%20investment%20executive%2C%20mid-30s%2C%20wearing%20round%20glasses%2C%20dark%20charcoal%20suit%20with%20white%20shirt%2C%20standing%20in%20modern%20Hong%20Kong%20office%20with%20panoramic%20harbor%20view%20through%20floor-to-ceiling%20windows%2C%20natural%20soft%20lighting%2C%20confident%20scholarly%20expression%2C%20professional%20corporate%20headshot%20style&width=200&height=200&seq=team-li-hk-office-2026&orientation=squarish', reportsTo: '向集团主席汇报' },
                { name: '张国强', title: '首席风控官', desc: '前香港证监会（SFC）高级监管官员，在金融合规与风险管理领域深耕逾22年。持有香港注册会计师（CPA）与金融风险管理师（FRM）双重资格认证，同时具备国际反洗钱师（CAMS）资质。曾参与制定多项香港资产管理行业监管指引，对跨境合规框架、机构风控体系搭建及压力测试方法论具有独到见解。先后在两家国际顶级托管银行担任亚太区合规总监，主导完成20余次监管审查零重大缺陷通过，被业内誉为“合规守护者”。', img: 'https://readdy.ai/api/search-image?query=Professional%20business%20portrait%20of%20Asian%20male%20compliance%20executive%2C%20late-40s%2C%20short%20neat%20hair%2C%20dark%20navy%20pinstripe%20suit%2C%20standing%20in%20elegant%20corporate%20boardroom%20with%20dark%20wood%20paneling%20and%20city%20skyline%20visible%2C%20serious%20composed%20expression%2C%20warm%20ambient%20lighting%2C%20professional%20executive%20portrait&width=200&height=200&seq=team-zhang-boardroom-2026&orientation=squarish', reportsTo: '向集团主席汇报' },
                { name: '王思敏', title: '首席技术官', desc: '前硅谷金融科技独角兽工程副总裁，人工智能与大规模分布式系统领域权威专家。斯坦福大学计算机科学博士，清华大学计算机科学与技术系本科。在NeurIPS、ICML、KDD等顶级学术会议发表论文20余篇，引用量超8,000次。曾领导百人工程师团队构建日处理数千万笔交易的实时风控与智能投研平台，系统可用性达99.99%。持有6项金融科技领域国际专利，涵盖高频交易引擎架构、图神经网络风控模型与大语言模型金融应用等前沿方向。', img: 'https://readdy.ai/api/search-image?query=Professional%20business%20portrait%20of%20Asian%20female%20technology%20executive%2C%20early-30s%2C%20sleek%20dark%20blazer%20over%20white%20blouse%2C%20modern%20minimalist%20tech%20office%20background%20with%20glass%20walls%20and%20subtle%20blue%20ambient%20lighting%2C%20confident%20warm%20smile%2C%20professional%20corporate%20headshot%20with%20soft%20bokeh&width=200&height=200&seq=team-wang-tech-office-2026&orientation=squarish', reportsTo: '向集团主席汇报' },
              ].map((person) => (
                <div key={person.name} className="bg-background-50 rounded-xl p-6 border border-background-300/30 text-center group hover:border-accent-500/30 transition-all duration-300">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 overflow-hidden ring-1 ring-background-300/40 group-hover:ring-accent-500/30 transition-all duration-300">
                    <img
                      src={person.img}
                      alt={person.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-100">{person.name}</h3>
                  <p className="text-xs text-accent-500 font-medium mt-1 mb-1">{person.title}</p>
                  {(person as any).reportsTo && (
                    <p className="text-[10px] text-foreground-600 font-light mb-3 tracking-wide">{(person as any).reportsTo}</p>
                  )}
                  <p className="text-xs text-foreground-500 leading-relaxed font-light">{person.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <FounderMessage />
          </div>
        </div>
      </section>

      {/* Global Layout */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Global Presence</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">全球布局</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { city: '香港', role: '全球总部', icon: 'ri-building-line', coords: '22.2793, 114.1628', status: 'OPEN', tz: 'HKT' },
                { city: '新加坡', role: '东南亚中心', icon: 'ri-building-2-line', coords: '1.3521, 103.8198', status: 'OPEN', tz: 'SGT' },
                { city: '上海', role: '中国内地总部', icon: 'ri-building-3-line', coords: '31.2304, 121.4737', status: 'OPEN', tz: 'CST' },
                { city: '伦敦', role: '欧洲代表处', icon: 'ri-building-4-line', coords: '51.5074, -0.1278', status: 'NEXT', tz: 'GMT' },
              ].map((loc) => (
                <div key={loc.city} className="bg-background-100 rounded-xl p-6 text-center border border-background-300/30 hover:border-accent-500/30 transition-all duration-300 group">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-500/10 border border-primary-500/15 flex items-center justify-center group-hover:border-accent-500/30 transition-colors duration-300">
                    <i className={`${loc.icon} text-lg text-primary-400 group-hover:text-accent-400 transition-colors duration-300`}></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-100 text-lg mono-data">{loc.city}</h4>
                  <p className="text-xs text-foreground-500 mt-1">{loc.role}</p>
                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${loc.status === 'OPEN' ? 'bg-emerald-400 animate-pulse-glow' : 'bg-accent-400/60'}`}></span>
                    <span className={`text-[10px] mono-data font-semibold ${loc.status === 'OPEN' ? 'text-emerald-400' : 'text-accent-400'}`}>
                      {loc.status} · {loc.tz}
                    </span>
                  </div>
                  <p className="text-[10px] text-foreground-600 mt-1 font-mono">{loc.coords}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Chairman Spotlight Card ── */
function ChairmanSpotlight() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [dataIdx, setDataIdx] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.25 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => setDataIdx(i => (i + 1) % 4), 2800);
    return () => clearInterval(timer);
  }, [visible]);

  const dataPoints = [
    { label: 'AUM GROWTH', val: '+312', suffix: '%', detail: '自集团成立以来' },
    { label: 'TOTAL CLIENTS', val: '270', suffix: '+', detail: '机构与家族客户' },
    { label: 'TEAM SIZE', val: '150', suffix: '+', detail: '覆盖5个全球办公室' },
    { label: 'LICENSES', val: '6', suffix: '', detail: 'SFC持牌主体' },
  ];

  const current = dataPoints[dataIdx];

  const { formatted: aumFormatted } = useCountUp({ target: 52.8, duration: 2200, suffix: 'B', decimals: 1, format: 'number', enabled: visible });
  const { formatted: yrFormatted } = useCountUp({ target: 2018, duration: 1500, suffix: '', format: 'compact', enabled: visible });

  return (
    <div ref={ref} className="relative mb-10">
      {/* Outer ambient glow - layered rings */}
      <div className="absolute -inset-6 rounded-3xl bg-gradient-to-r from-accent-500/3 via-primary-500/5 to-accent-500/3 blur-2xl animate-pulse-glow" style={{ animationDuration: '5s' }}></div>
      <div className="absolute -inset-3 rounded-3xl border border-accent-500/10 animate-pulse-glow" style={{ animationDuration: '3.5s', animationDelay: '0.6s' }}></div>

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-background-50 via-background-100 to-background-50 rounded-2xl border border-background-300/40 overflow-hidden">
        {/* Top prestige bar */}
        <div className="h-1.5 bg-gradient-to-r from-accent-500/80 via-primary-500/50 to-accent-500/80"></div>

        {/* Chairman badge on top right */}
        <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
          <div className="flex items-center gap-2 bg-background-50/90 backdrop-blur-sm rounded-full border border-accent-500/25 px-4 py-1.5">
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse-glow"></span>
            <span className="text-[10px] md:text-xs mono-data font-semibold text-accent-400 tracking-widest">FOUNDER &amp; CHAIRMAN</span>
          </div>
        </div>

        <div className="p-8 md:p-10 lg:p-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-start">

            {/* LEFT: Avatar & Identity Block */}
            <div className="shrink-0 flex flex-col items-center lg:items-start">
              <div className="relative">
                {/* Triple pulse ring */}
                <div className="absolute -inset-2 rounded-full border-2 border-accent-500/15 animate-pulse-glow" style={{ animationDuration: '3s' }}></div>
                <div className="absolute -inset-3.5 rounded-full border border-accent-500/10 animate-pulse-glow" style={{ animationDuration: '3s', animationDelay: '0.8s' }}></div>
                <div className="absolute -inset-5 rounded-full border border-primary-500/8 animate-pulse-glow" style={{ animationDuration: '4s', animationDelay: '1.5s' }}></div>

                {/* Avatar */}
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden ring-3 ring-accent-500/35 relative z-10">
                  <img
                    src="https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/041ee6c9-00c0-4544-bb15-f0fdaa2ca6c6_compressed_2.webp"
                    alt="白子晨"
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Live status indicators */}
                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-400 border-2 border-background-50 z-20 animate-pulse-glow"></span>
                <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-accent-400/60 border-2 border-background-50 z-20 animate-pulse-glow" style={{ animationDelay: '1.2s' }}></span>
              </div>

              {/* Identity block */}
              <div className="mt-6 text-center lg:text-left">
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 tracking-tight">白子晨</h3>
                <div className="mt-2 inline-flex items-center gap-1.5 bg-accent-500/10 border border-accent-500/20 rounded-full px-3 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                  <p className="text-xs text-accent-400 font-medium whitespace-nowrap">集团主席兼首席执行官</p>
                </div>
                <div className="mt-3 flex items-center gap-2 justify-center lg:justify-start">
                  <span className="text-[10px] font-light text-foreground-600 bg-background-200/60 rounded-full px-2.5 py-0.5">剑桥大学 · 数学一等荣誉学士</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-500/30"></span>
                  <span className="text-[10px] font-light text-foreground-600 bg-background-200/60 rounded-full px-2.5 py-0.5">斯坦福大学 · 计算金融硕士</span>
                </div>
              </div>
            </div>

            {/* RIGHT: Bio + Live Data */}
            <div className="flex-1 min-w-0 flex flex-col justify-between">
              {/* Bio with more '牛逼' energy */}
              <div>
                <div className="mb-5">
                  <i className="ri-double-quotes-l text-4xl text-accent-500/20"></i>
                </div>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed font-light">
                  前全球顶尖对冲基金量化研究主管，拥有逾<span className="text-accent-400 font-medium">12年</span>量化投资与资产管理行业经验。
                  剑桥大学数学系<span className="text-accent-400 font-medium">一等荣誉学士</span>，斯坦福大学计算金融硕士。
                  曾主导管理超<span className="text-accent-400 font-medium">80亿美元</span>量化资产，其研发的机器学习驱动策略在多个市场周期中实现<span className="text-accent-400 font-medium">年化回报超18%</span>的卓越表现。
                  2018年创立蚂蚁链控股集团，以<span className="text-accent-400 font-medium">「量化驱动资管」</span>为使命，仅用6年将集团打造为亚太区量化资产管理赛道标杆企业。
                  被《亚洲金融》评为<span className="text-accent-400 font-medium">「亚洲金融科技40位40岁以下领袖」</span>，并连续三年入选《彭博商业周刊》「香港金融新势力」榜单。
                </p>
              </div>

              {/* Live data ticker strip */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* AUM (live) */}
                <div className="bg-background-200/60 rounded-lg p-3 text-center">
                  <p className="text-[9px] mono-data text-foreground-600 tracking-wider mb-1">AUM</p>
                  <p className="text-sm md:text-base mono-data font-bold text-foreground-100 tabular-nums">{visible ? aumFormatted : '--'}</p>
                  <span className="flex items-center justify-center gap-1 mt-0.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                    <span className="text-[9px] mono-data text-emerald-400/70">LIVE</span>
                  </span>
                </div>

                {/* Established */}
                <div className="bg-background-200/60 rounded-lg p-3 text-center">
                  <p className="text-[9px] mono-data text-foreground-600 tracking-wider mb-1">EST.</p>
                  <p className="text-sm md:text-base mono-data font-bold text-foreground-100 tabular-nums">{visible ? yrFormatted : '--'}</p>
                  <span className="block text-[9px] mono-data text-foreground-600 mt-0.5">HONG KONG</span>
                </div>

                {/* Rotating data point */}
                <div className="bg-background-200/60 rounded-lg p-3 text-center overflow-hidden relative" key={dataIdx}>
                  <p className="text-[9px] mono-data text-foreground-600 tracking-wider mb-1">{current.label}</p>
                  <p className="text-sm md:text-base mono-data font-bold text-accent-400 tabular-nums animate-fade-in-up">
                    {current.val}<span className="text-xs">{current.suffix}</span>
                  </p>
                  <span className="block text-[9px] mono-data text-foreground-600 mt-0.5 truncate">{current.detail}</span>
                </div>

                {/* Status */}
                <div className="bg-background-200/60 rounded-lg p-3 text-center">
                  <p className="text-[9px] mono-data text-foreground-600 tracking-wider mb-1">STATUS</p>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                    <span className="text-sm mono-data font-bold text-emerald-400">ACTIVE</span>
                  </div>
                  <span className="block text-[9px] mono-data text-foreground-600 mt-0.5">CHAIRMAN OFFICE</span>
                </div>
              </div>

              {/* Bottom tech bar */}
              <div className="mt-6 flex flex-wrap items-center gap-3 pt-4 border-t border-background-300/30">
                <span className="text-[9px] mono-data text-foreground-600 tracking-widest">EXECUTIVE_CHAIRMAN::ANTCHAIN_GROUP</span>
                <span className="w-px h-3 bg-background-400/30 hidden sm:block"></span>
                <span className="text-[9px] mono-data text-accent-400/70 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400/50"></span>
                  HK_SFC_LICENSED
                </span>
                <span className="w-px h-3 bg-background-400/30 hidden sm:block"></span>
                <span className="text-[9px] mono-data text-foreground-600 ml-auto">
                  BOARD_SINCE: 2018.03
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scan line effect */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/40 to-transparent"></div>
      </div>
    </div>
  );
}

/* ── Live Metrics Bar for About Page ── */
function AboutMetrics() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const metrics = [
    { label: '成立年份', target: 2018, suffix: '', live: false, liveVal: 0, liveRange: 0 },
    { label: '管理规模', target: 50, suffix: 'B+', live: true, liveVal: 52.8, liveRange: 1.5, liveSuffix: 'B' },
    { label: '持牌主体', target: 6, suffix: '', live: false, liveVal: 0, liveRange: 0 },
    { label: '全球团队', target: 150, suffix: '+', live: true, liveVal: 153.4, liveRange: 2, liveSuffix: '' },
  ];

  return (
    <div ref={ref} className="mt-8 terminal-module p-4 md:p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2 h-2 rounded-full bg-red-400/40"></span>
        <span className="w-2 h-2 rounded-full bg-accent-400/40"></span>
        <span className="w-2 h-2 rounded-full bg-emerald-400/40"></span>
        <span className="text-[9px] mono-data text-foreground-600 tracking-wider ml-1">LIVE_GROUP_METRICS</span>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow ml-auto"></span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((m) => (
          <AboutMetricItem key={m.label} metric={m} visible={visible} />
        ))}
      </div>
    </div>
  );
}

function AboutMetricItem({ metric, visible }: { metric: { label: string; target: number; suffix: string; live: boolean; liveVal: number; liveRange: number; liveSuffix?: string }; visible: boolean }) {
  const { formatted } = useCountUp({ target: metric.target, duration: 1800, suffix: metric.suffix, format: 'compact', enabled: visible });
  const { formatted: liveFormatted, flash } = useLiveData({
    initial: metric.liveVal,
    range: metric.liveRange,
    decimals: 1,
    interval: 3000,
    suffix: metric.liveSuffix || '',
  });

  return (
    <div className="text-center">
      <span className="block text-[9px] mono-data text-foreground-600 tracking-wide uppercase mb-1">{metric.label}</span>
      <span className="block text-lg md:text-xl mono-data font-bold text-foreground-100 tabular-nums">
        {visible ? formatted : '--'}
      </span>
      {metric.live && (
        <span className={`block text-[10px] mono-data mt-0.5 transition-all duration-300 ${flash ? 'text-accent-400 data-tick' : 'text-emerald-400/70'}`}>
          RT: {liveFormatted}
        </span>
      )}
    </div>
  );
}

/* ── Strategy Card with Live Indicator ── */
function AboutStrategyCard({ icon, title, desc, accent, liveLabel, liveVal, liveSuffix }: {
  icon: string; title: string; desc: string; accent: string; liveLabel: string; liveVal: number; liveSuffix: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { formatted } = useCountUp({ target: liveVal, duration: 2000, suffix: liveSuffix, format: 'compact', enabled: visible });

  return (
    <div ref={ref} className={`bg-background-100 rounded-xl border-l-2 ${accent} p-6 md:p-8 border border-background-300/30 group hover:border-accent-500/20 transition-all duration-300`}>
      <div className="w-11 h-11 rounded-lg bg-primary-500/10 border border-primary-500/15 flex items-center justify-center mb-5 group-hover:border-accent-500/30 transition-colors duration-300">
        <i className={`${icon} text-lg text-primary-400 group-hover:text-accent-400 transition-colors duration-300`}></i>
      </div>
      <h3 className="text-lg font-heading font-bold text-foreground-100 mb-3">{title}</h3>
      <p className="text-sm text-foreground-500 leading-relaxed font-light mb-4">{desc}</p>

      {/* Live indicator */}
      <div className="flex items-center gap-2 pt-3 border-t border-background-300/30">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
        <span className="text-[9px] mono-data text-foreground-600 tracking-wide">{liveLabel}</span>
        <span className="text-xs mono-data font-bold text-accent-400 tabular-nums ml-auto">
          {visible ? formatted : '--'}
        </span>
      </div>
    </div>
  );
}
